<script setup>
// Por que o bot desta conversa está calado — e o interruptor para devolvê-lo.
//
// O botão do cabeçalho diz "pausado"; este painel diz por quem, desde quando, e o que
// aconteceu antes: o rastro de `conversation_events` do runtime, traduzido para quem atende.
// É a resposta a "por que o agente parou de responder essa pessoa?", que hoje só existe no
// banco do runtime.
//
// Só monta quando a conversa é de PowerAgent (modo `native` do cabeçalho) e carrega no
// CLIQUE, como o follow-up: uma ida ao runtime por conversa aberta seria custo permanente
// de uma tela pouco usada.
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { useAgentBot } from 'dashboard/composables/useAgentBot';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { fraseDoEvento, estadoDoEvento } from 'dashboard/helper/handoffTrace';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const store = useStore();
const { definirAtivo, causaDaPausa } = useAgentBot();

const dialogRef = ref(null);
const carregando = ref(false);
const salvando = ref(false);
const erro = ref('');
const agente = ref(null);
const estado = ref({});
const eventos = ref([]);

const semAgente = computed(
  () => !carregando.value && !erro.value && !agente.value
);
const ativo = computed(() => estado.value.bot_active !== false);
const causa = computed(() => causaDaPausa(estado.value.silenciado_por));

function horaDe(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

// "Desde quando" e, na dormência, "até quando" — montado aqui e não no template, porque o
// linter de i18n barra o separador solto, e ele tem razão: é a única parte sem tradução.
const desdeQuando = computed(() => {
  if (ativo.value || !estado.value.paused_at) return '';
  const desde = t('CONVERSATION.AGENT_BOT.SINCE', {
    when: horaDe(estado.value.paused_at),
  });
  if (!estado.value.silencio_ate) return desde;
  const ate = t('CONVERSATION.AGENT_BOT.UNTIL', {
    when: horaDe(estado.value.silencio_ate),
  });
  return `${desde} · ${ate}`;
});
// O time do handoff pelo nome, quando o store o conhece; o id cru é o que sobra quando
// o time foi apagado — e ainda é mais verdadeiro do que esconder a linha.
const timeDoHandoff = computed(() => {
  const id = estado.value.handoff_time_id;
  if (!id) return '';
  return store.getters['teams/getTeam'](id)?.name || `#${id}`;
});

async function carregar() {
  carregando.value = true;
  erro.value = '';
  try {
    const [bot, rastro] = await Promise.all([
      ConversationApi.agentBot(props.conversationId),
      ConversationApi.agentBotEvents(props.conversationId),
    ]);
    agente.value = bot.data.agent;
    estado.value = bot.data;
    eventos.value = rastro.data.eventos || [];
  } catch (e) {
    // "runtime fora do ar" e "não carregou" não podem virar o mesmo cinza: o primeiro é
    // problema da plataforma, e o botão do cabeçalho continua mostrando o último espelho.
    erro.value =
      e?.response?.data?.error === 'runtime_unreachable'
        ? t('CONVERSATION.AGENT_BOT.RUNTIME_DOWN')
        : t('CONVERSATION.AGENT_BOT.LOAD_ERROR');
  } finally {
    carregando.value = false;
  }
}

function abrir() {
  dialogRef.value?.open();
  carregar();
}

async function alternar() {
  salvando.value = true;
  try {
    await definirAtivo(props.conversationId, !ativo.value);
    // Relê: o evento novo entra no rastro e o estado vem lido de volta do runtime.
    await carregar();
  } catch (e) {
    // já avisado pelo composable
  } finally {
    salvando.value = false;
  }
}

// O motivo que acompanha o evento (`detalhe.motivo`): quem pausou pela tela, o que o
// modelo disse ao pedir humano. É texto de gente, e vai cru.
function motivoDe(evento) {
  const motivo = evento?.detalhe?.motivo;
  return typeof motivo === 'string' ? motivo : '';
}
</script>

<template>
  <span>
    <Button
      v-tooltip="t('CONVERSATION.AGENT_BOT.TITLE')"
      icon="i-lucide-hand"
      slate
      faded
      sm
      @click="abrir"
    />

    <Dialog
      ref="dialogRef"
      :title="t('CONVERSATION.AGENT_BOT.TITLE')"
      :show-cancel-button="false"
      :show-confirm-button="false"
      width="lg"
    >
      <div v-if="carregando" class="py-6 text-sm text-center text-n-slate-11">
        {{ t('CONVERSATION.AGENT_BOT.LOADING') }}
      </div>

      <p
        v-else-if="erro"
        class="p-3 text-sm rounded-lg bg-n-ruby-2 text-n-ruby-11"
      >
        {{ erro }}
      </p>

      <p v-else-if="semAgente" class="py-6 text-sm text-center text-n-slate-11">
        {{ t('CONVERSATION.AGENT_BOT.NO_AGENT') }}
      </p>

      <div v-else class="flex flex-col gap-4">
        <!-- O estado primeiro, âmbar quando pausado: é o que alguém veio ver, e o botão ao
             lado é o que veio fazer. -->
        <div
          class="flex flex-wrap items-center justify-between gap-3 p-3 border rounded-xl"
          :class="ativo ? 'border-n-weak' : 'border-n-amber-6 bg-n-amber-2'"
        >
          <div class="min-w-0">
            <p class="text-sm font-medium text-n-slate-12">
              {{
                ativo
                  ? t('CONVERSATION.AGENT_BOT.ON_TITLE', { name: agente.name })
                  : t('CONVERSATION.AGENT_BOT.OFF_TITLE', { name: agente.name })
              }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ ativo ? t('CONVERSATION.AGENT_BOT.ON_HINT') : causa }}
            </p>
            <p v-if="desdeQuando" class="mt-0.5 text-[11px] text-n-slate-10">
              {{ desdeQuando }}
            </p>
          </div>
          <Button
            :label="
              ativo
                ? t('CONVERSATION.AGENT_BOT.PAUSE_BUTTON')
                : t('CONVERSATION.AGENT_BOT.RESUME_BUTTON')
            "
            :is-loading="salvando"
            :amber="ativo"
            :teal="!ativo"
            sm
            class="shrink-0"
            @click="alternar"
          />
        </div>

        <!-- O pedido de humano em aberto, quando há: categoria e time são o que quem
             recebe a conversa precisa para saber se ela é sua. -->
        <div
          v-if="estado.handoff_solicitado_em"
          class="p-3 text-xs border rounded-lg border-n-weak text-n-slate-11"
        >
          <p class="font-medium text-n-slate-12">
            {{
              t('CONVERSATION.AGENT_BOT.HANDOFF_REQUESTED', {
                when: horaDe(estado.handoff_solicitado_em),
              })
            }}
          </p>
          <p v-if="estado.handoff_categoria" class="mt-0.5">
            {{
              t('CONVERSATION.AGENT_BOT.HANDOFF_CATEGORY', {
                categoria: estado.handoff_categoria,
              })
            }}
          </p>
          <p v-if="timeDoHandoff" class="mt-0.5">
            {{
              t('CONVERSATION.AGENT_BOT.HANDOFF_TEAM', { team: timeDoHandoff })
            }}
          </p>
        </div>

        <div>
          <h4
            class="mb-2 text-xs font-semibold tracking-wide uppercase text-n-slate-11"
          >
            {{ t('CONVERSATION.AGENT_BOT.HISTORY') }}
          </h4>

          <p
            v-if="!eventos.length"
            class="p-3 text-xs border border-dashed rounded-lg border-n-weak text-n-slate-10"
          >
            {{ t('CONVERSATION.AGENT_BOT.NO_EVENTS') }}
          </p>

          <ul v-else class="flex flex-col gap-1.5">
            <li
              v-for="(evento, i) in eventos"
              :key="evento.id || i"
              class="flex items-start gap-2.5 p-2.5 border rounded-lg border-n-weak"
            >
              <i
                class="mt-0.5 text-sm shrink-0"
                :class="{
                  'i-lucide-pause text-n-amber-11':
                    estadoDoEvento(evento) === 'pausa',
                  'i-lucide-play text-n-teal-11':
                    estadoDoEvento(evento) === 'retomada',
                  'i-lucide-hand text-n-iris-11':
                    estadoDoEvento(evento) === 'pedido',
                  'i-lucide-minus-circle text-n-slate-10':
                    estadoDoEvento(evento) === 'neutro',
                }"
              />
              <div class="min-w-0">
                <p class="text-sm text-n-slate-12">
                  {{ fraseDoEvento(evento) }}
                </p>
                <p
                  v-if="motivoDe(evento)"
                  class="mt-0.5 text-xs text-n-slate-11 break-words"
                >
                  {{ motivoDe(evento) }}
                </p>
                <p class="mt-1 text-[11px] text-n-slate-10">
                  {{ horaDe(evento.em) }}
                </p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </Dialog>
  </span>
</template>
